/**
 * Página /reset-password — cambia la contraseña tras click en el link
 * que se envía desde /forgot-password.
 *
 * Flujo:
 * 1. Supabase emite un link con un token de recovery en el hash:
 *    `https://app.kaptar.com.co/reset-password#access_token=...&type=recovery`
 * 2. El SDK de Supabase JS detecta el hash al cargar y emite el evento
 *    `PASSWORD_RECOVERY` via `onAuthStateChange`. Ese evento deja la
 *    sesión en un estado "elegible para cambiar password" pero NO
 *    autenticada — el user sigue sin acceso al resto de la app.
 * 3. Mostramos form con nueva password + confirmar.
 * 4. Submit → `supabase.auth.updateUser({ password })`.
 * 5. Al éxito, Supabase emite `USER_UPDATED` y redireccionamos a /login
 *    con un mensaje de éxito (no auto-login para forzar reintento limpio
 *    con la nueva password).
 *
 * Edge cases:
 * - Si el user llega a /reset-password sin el hash (ej. typed URL):
 *   mostramos mensaje + link de vuelta a /forgot-password.
 * - Si el token expiró: Supabase responde con error; mostramos mensaje
 *   con link a solicitar uno nuevo.
 */
import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { describePasswordError } from "@/lib/auth-errors";
import { supabase } from "@/lib/supabase";

const MIN_PASSWORD_LENGTH = 8;

export function ResetPassword() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [recoveryEventReceived, setRecoveryEventReceived] = useState(false);

  // Detectar el evento PASSWORD_RECOVERY emitido por Supabase al
  // parsear el hash. Damos una ventana de 2s — si no llega, asumimos
  // que el user entró sin recovery token válido.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setRecoveryReady(true);
        setRecoveryEventReceived(true);
      }
    });

    const timeout = window.setTimeout(() => {
      setRecoveryEventReceived(true);
    }, 2000);

    return () => {
      subscription.unsubscribe();
      window.clearTimeout(timeout);
    };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorMsg(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }

    if (password !== confirm) {
      setErrorMsg("Las dos contraseñas no coinciden.");
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setIsSubmitting(false);

    if (error) {
      // Mismo traductor que /aceptar-invitacion: qué falló y qué hacer.
      const copy = describePasswordError(error, MIN_PASSWORD_LENGTH);
      setErrorMsg(
        copy.recoveryHint
          ? `${copy.message} Solicita otro enlace desde «¿Olvidaste tu contraseña?».`
          : copy.message,
      );
      return;
    }

    // No auto-login. Cerramos cualquier sesión residual del recovery
    // y mandamos al login para reintento limpio con la nueva password.
    await supabase.auth.signOut();
    navigate("/login?password_reset=ok", { replace: true });
  }

  if (recoveryEventReceived && !recoveryReady) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-surface p-6">
        <div className="w-full max-w-md">
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="mb-2 text-lg font-bold text-amber-900">Link inválido o expirado</h2>
            <p className="text-sm text-amber-800">
              No detectamos un token de recuperación válido. El enlace puede haber expirado o estar
              mal formado.
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Link
                to="/forgot-password"
                className="rounded-md bg-cta-600 px-4 py-2 text-center text-sm font-semibold text-white hover:bg-cta-700"
              >
                Solicitar otro enlace
              </Link>
              <Link
                to="/login"
                className="rounded-md border border-slate-300 bg-white px-4 py-2 text-center text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Volver a Ingresar
              </Link>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md">
        <header className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600">
            <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
              <path d="M12 3L4 8l8 5 8-5-8-5z" fill="white" opacity="0.9" />
              <path d="M4 13l8 5 8-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Kaptar</h1>
            <p className="text-xs text-slate-500">Restablecer contraseña</p>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs"
          aria-labelledby="reset-title"
        >
          <h2 id="reset-title" className="mb-1 text-lg font-bold text-slate-900">
            Crear nueva contraseña
          </h2>
          <p className="mb-6 text-sm text-slate-600">
            Mínimo {MIN_PASSWORD_LENGTH} caracteres. Usa una combinación de letras, números y
            símbolos.
          </p>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Nueva contraseña</span>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
            />
          </label>

          <label className="mb-5 block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Confirmar contraseña
            </span>
            <input
              type="password"
              required
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-600 focus:ring-1 focus:ring-brand-600 focus:outline-hidden"
            />
          </label>

          {errorMsg && (
            <div
              role="alert"
              className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !recoveryReady}
            className="w-full rounded-md bg-cta-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? "Guardando…" : "Guardar nueva contraseña"}
          </button>

          {!recoveryReady && !recoveryEventReceived && (
            <p className="mt-3 text-center text-xs text-slate-500">
              Verificando enlace de recuperación…
            </p>
          )}
        </form>
      </div>
    </main>
  );
}
